<template>
  <li
    class="list-group-item searched-department"
    :data-original-title="filteredDepartment.fullPathWithoutRoot"
    :data-department-id-str="filteredDepartment.departmentIdStr"
  >
    <a
      ref="link"
      href="#"
      :class="{ selected: isSelected, deny: isDeny, allow: !isDeny }"
      @keydown="onKeydown"
      @keypress="onKeypress"
      @click.prevent="onClick"
      @blur="onBlur"
    >
      <span v-if="isDeeperThenRootChild">...</span>
      <template v-for="(splittedName, index) in splittedNames">
        <em
          v-if="index !== 0"
          :key="`keyword_${index}`"
          v-text="keyword"
          style="background: yellow"
        />
        <span :key="`name_${index}`" v-text="splittedName" />
      </template>
    </a>
  </li>
</template>

<script lang="ts">
import type { FilteredDepartment } from "./values/FilteredDepartment";
import { VK_UP, VK_DOWN } from "./values/VirtualKey";
import { computed, defineComponent, ref, type PropType } from "vue";

export default defineComponent({
  name: "filtered-departments-tree-node",
  props: {
    filteredDepartment: { type: Object as PropType<FilteredDepartment>, required: true },
    keyword: { type: String, required: true },
    selectedDepartmentIdStr: { type: String, required: true },
    nextFocusHolder: { type: String, required: true },
    denyIdList: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props, { emit }) {
    // NOTE: 旧コードでは data として宣言され一度も代入されない（常に null・未参照）dead data。
    // テンプレートの ref="link" 名と一致するため、Vue 2.7 では setup() から return すると
    // a 要素が束縛されるが、どこからも参照されないため挙動への影響はない。
    const link = ref<HTMLAnchorElement | null>(null);

    const splittedNames = computed((): string[] => {
      return props.filteredDepartment.name.split(props.keyword);
    });

    const isDeeperThenRootChild = computed((): boolean => {
      return props.filteredDepartment.depth > 1;
    });

    const isSelected = computed((): boolean => {
      return props.selectedDepartmentIdStr === props.filteredDepartment.departmentIdStr;
    });

    const isDeny = computed((): boolean => {
      return props.denyIdList.includes(props.filteredDepartment.departmentIdStr);
    });

    const selectDepartment = (): void => {
      emit("selectDepartment", props.filteredDepartment.departmentIdStr);
    };

    const onKeydown = (event: KeyboardEvent): void => {
      // Note: event.code はIE11非対応
      switch (event.keyCode) {
        case VK_DOWN:
          event.preventDefault();
          emit("focusNext");
          break;
        case VK_UP:
          event.preventDefault();
          emit("focusPrev");
          break;
      }
    };

    const onKeypress = (): void => {
      selectDepartment();
    };

    const onClick = (): void => {
      selectDepartment();
    };

    const onBlur = () => {
      emit("blur");
    };

    return {
      link,
      splittedNames,
      isDeeperThenRootChild,
      isSelected,
      isDeny,
      selectDepartment,
      onKeydown,
      onKeypress,
      onClick,
      onBlur,
    };
  },
});
// 要注意: Haxe 経由での JQuery からのDOM変更が残っている
</script>
<style scoped lang="scss">
$allow: #c5ebef;
$allow-hover: #9bdde2;
$deny: white;
$deny-hover: #e5e5e5;

.list-group-item {
  display: flex;
  padding: 0px;
}

a {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 10px;
  width: 100%;
  color: #34495e;

  &.deny {
    background-color: $deny;

    .selected {
      background-color: $deny-hover;
    }

    &:hover {
      background-color: $deny-hover;
    }
    &:focus {
      background-color: $deny-hover;
    }
  }

  &.allow {
    background-color: $allow;

    .selected {
      background-color: $allow-hover;
    }

    &:hover {
      background-color: $allow-hover;
    }
    &:focus {
      background-color: $allow-hover;
    }
  }
  &:focus {
    outline: none;
    text-decoration: none;
  }
  &:hover {
    text-decoration: none;
  }
}
</style>
